import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, fmtAmount as fmt, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Loading,
  Panel,
  Table,
  TableSearch,
  Td,
  Th,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";
import { CommissionForm } from "./shared";

export function Commissions({
  slug,
  currency,
  role,
}: { slug: string; currency: string; role: string }) {
  const [showNew, setShowNew] = useState(false);
  const del = useDeleteRecord(slug);

  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });
  const clients = useQuery({
    queryKey: ["clients", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } })),
  });
  const contractors = useQuery({
    queryKey: ["contractors", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].contractors.$get({ param: { slug } })),
  });
  const summary = useQuery({
    queryKey: ["commissions-summary", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].commissions.summary.$get({ param: { slug }, query: {} })),
  });

  const commissions = useResource({
    key: ["commissions", slug],
    list: async () =>
      unwrap(await api.api.c[":slug"].commissions.$get({ param: { slug }, query: {} })),
    create: async (body: Record<string, unknown>) =>
      unwrap(await api.api.c[":slug"].commissions.$post({ param: { slug }, json: body as never })),
    alsoInvalidate: [["commissions-summary", slug]],
  });

  const commissionsTable = useTable(commissions.query.data?.commissions ?? [], {
    filter: (cm, q) =>
      `${cm.description} ${cm.clientName ?? ""} ${cm.contractorName ?? ""} ${cm.projectName ?? ""} ${cm.reference ?? ""}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      date: (cm) => cm.earnedOn,
      description: (cm) => cm.description.toLowerCase(),
      from: (cm) => (cm.clientName ?? cm.contractorName ?? "").toLowerCase(),
      against: (cm) => (cm.scope === "project" ? (cm.projectName ?? "") : "Company").toLowerCase(),
      amount: (cm) => Number(cm.amount),
    },
  });

  const summaryTable = useTable(summary.data?.rows ?? [], {
    filter: (r, q) =>
      (r.scope === "project" ? (r.projectName ?? "") : "Company-wide")
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      against: (r) =>
        (r.scope === "project" ? (r.projectName ?? "") : "Company-wide").toLowerCase(),
      entries: (r) => r.entries,
      earned: (r) => Number(r.total),
    },
  });

  return (
    <>
      <Panel
        title="Commissions earned"
        action={
          <Button
            onClick={() => {
              setShowNew(!showNew);
              commissions.clearErrors();
            }}
          >
            {showNew ? "Cancel" : "Record commission"}
          </Button>
        }
      >
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          Money the company earns outside a contract — a referral cut, a finder's fee, a management
          percentage. The mirror of an expense: it belongs either to a project or to the company,
          and it's voided rather than deleted once recorded.
        </p>

        {showNew && (
          <CommissionForm
            slug={slug}
            currency={currency}
            projects={projects.data?.projects ?? []}
            clients={clients.data?.clients ?? []}
            contractors={contractors.data?.contractors ?? []}
            errors={commissions.fieldErrors}
            formError={commissions.formError}
            saving={commissions.saving}
            onSubmit={async (body) => {
              await commissions.create(body);
              setShowNew(false);
            }}
          />
        )}

        {del.error && <ErrorNote>{del.error}</ErrorNote>}

        {commissions.query.isPending ? (
          <Loading />
        ) : commissions.query.isError ? (
          <ErrorNote>{(commissions.query.error as Error).message}</ErrorNote>
        ) : commissions.query.data.commissions.length === 0 ? (
          <Empty title="No commissions yet">
            Record the first one when it's earned, not when it lands.
          </Empty>
        ) : (
          <>
            <TableSearch
              value={commissionsTable.query}
              onChange={commissionsTable.setQuery}
              placeholder="Filter commissions…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="date"
                    active={commissionsTable.sortKey === "date"}
                    dir={commissionsTable.dir}
                    onSort={commissionsTable.toggleSort}
                  >
                    Date
                  </Th>
                  <Th
                    sortKey="description"
                    active={commissionsTable.sortKey === "description"}
                    dir={commissionsTable.dir}
                    onSort={commissionsTable.toggleSort}
                  >
                    Description
                  </Th>
                  <Th
                    sortKey="from"
                    active={commissionsTable.sortKey === "from"}
                    dir={commissionsTable.dir}
                    onSort={commissionsTable.toggleSort}
                  >
                    From
                  </Th>
                  <Th
                    sortKey="against"
                    active={commissionsTable.sortKey === "against"}
                    dir={commissionsTable.dir}
                    onSort={commissionsTable.toggleSort}
                  >
                    Against
                  </Th>
                  <Th
                    right
                    sortKey="amount"
                    active={commissionsTable.sortKey === "amount"}
                    dir={commissionsTable.dir}
                    onSort={commissionsTable.toggleSort}
                  >
                    Amount
                  </Th>
                  {role === "owner" && <Th />}
                </>
              }
            >
              {commissionsTable.rows.map((cm) => (
                <tr
                  key={cm.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td mono muted>
                    {cm.earnedOn}
                  </Td>
                  <Td>
                    {cm.description}
                    {cm.status === "void" && (
                      <span className="ml-2 text-xs text-red-600">voided</span>
                    )}
                    {cm.reference && (
                      <span className="ml-2 text-xs text-neutral-400">{cm.reference}</span>
                    )}
                  </Td>
                  <Td muted>{cm.clientName ?? cm.contractorName ?? "—"}</Td>
                  {/* The project/company split, stated rather than inferred. */}
                  <Td muted>{cm.scope === "project" ? (cm.projectName ?? "—") : "Company"}</Td>
                  <Td right mono>
                    {fmt(cm.amount, cm.currency)}
                  </Td>
                  {role === "owner" && (
                    <Td right>
                      <DeleteButton
                        busy={del.deleting}
                        onConfirm={() => del.remove("commission", cm.id)}
                      />
                    </Td>
                  )}
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>

      <Panel title="Earned by project">
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          Voided entries are excluded here but stay in the list above — this answers what was
          earned, not what was ever typed in.
        </p>

        {summary.isPending ? (
          <Loading />
        ) : !summary.data?.rows.length ? (
          <Empty title="Nothing to summarise yet" />
        ) : (
          <>
            <TableSearch
              value={summaryTable.query}
              onChange={summaryTable.setQuery}
              placeholder="Filter summary…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="against"
                    active={summaryTable.sortKey === "against"}
                    dir={summaryTable.dir}
                    onSort={summaryTable.toggleSort}
                  >
                    Against
                  </Th>
                  <Th
                    right
                    sortKey="entries"
                    active={summaryTable.sortKey === "entries"}
                    dir={summaryTable.dir}
                    onSort={summaryTable.toggleSort}
                  >
                    Entries
                  </Th>
                  <Th
                    right
                    sortKey="earned"
                    active={summaryTable.sortKey === "earned"}
                    dir={summaryTable.dir}
                    onSort={summaryTable.toggleSort}
                  >
                    Earned
                  </Th>
                </>
              }
            >
              {summaryTable.rows.map((r) => (
                <tr
                  key={r.projectId ?? "company"}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>{r.scope === "project" ? (r.projectName ?? "—") : "Company-wide"}</Td>
                  <Td right mono muted>
                    {r.entries}
                  </Td>
                  <Td right mono>
                    {fmt(r.total, currency)}
                  </Td>
                </tr>
              ))}
            </Table>
            {summary.data.totals && (
              <p className="mt-3 text-sm text-neutral-500">
                Total earned{" "}
                <span className="tabular text-neutral-900">
                  {fmt(summary.data.totals.total, currency)}
                </span>
              </p>
            )}
          </>
        )}
      </Panel>
    </>
  );
}
